/**
 * Gruppen, Bereiche, Favoriten und Einstellungen der Seitenleiste (N-01).
 * Im Anpassen-Modus tragen die Zeilen Knoepfe zum Verschieben, Ausblenden und
 * Anheften; Gruppennamen werden zu Eingabefeldern.
 */
import { Fragment, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  ArrowDown, ArrowUp, BarChart3, CalendarDays, CheckSquare, ChevronDown, Dumbbell, Eye, EyeOff, FolderKanban, Layers, LayoutGrid, Library,
  Lightbulb, ListChecks, NotebookPen, Plus, Repeat, RotateCcw, Salad, Settings, Star, Target, type LucideIcon,
} from 'lucide-react';

import { useAreas } from '@/modules/areas/useAreas';
import { useAlleListen } from '@/modules/listen/useListen';
import { AreaDot } from '@/modules/areas/AreaDot';
import { pfad } from '@/app/fokus';
import {
  aufloesen, benenneGruppe, favoritenListe, toggleEingeklappt, toggleFavorit, toggleVersteckt, verschiebeEintrag, verschiebeGruppe,
  zuruecksetzen, type AufgeloesteGruppe, type GruppenId, type NavEintrag, type SeitenleistenEinstellung,
} from '@/domain/oberflaeche/seitenleiste';
import { cn } from '@/lib/utils/cn';
import { de } from '@/i18n/de';

const SYMBOLE: Record<string, LucideIcon> = {
  uebersicht: LayoutGrid, kalender: CalendarDays, aufgaben: CheckSquare, gewohnheiten: Repeat,
  ziele: Target, projekte: FolderKanban, journal: NotebookPen, rueckblick: RotateCcw, statistik: BarChart3,
  listen: ListChecks, wissen: Lightbulb, notizen: Library,
};

const linkKlasse = (isActive: boolean) => cn(
  'flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
  isActive ? 'bg-surface-raised font-medium text-ink' : 'text-ink-muted hover:bg-surface-raised hover:text-ink',
);
const unterKlasse = (isActive: boolean) => cn(
  'ml-6 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors',
  isActive ? 'bg-surface-raised font-medium text-ink' : 'text-ink-faint hover:bg-surface-raised hover:text-ink',
);
const kleinerKnopf = 'grid size-6 shrink-0 place-items-center rounded text-ink-faint hover:bg-surface-raised hover:text-ink disabled:opacity-30';

type Aendern = (f: (e: SeitenleistenEinstellung) => SeitenleistenEinstellung) => Promise<void>;

export function SeitenleistenNav({ einstellung, aendern, anpassen, schmal, schliessen }: {
  einstellung: SeitenleistenEinstellung; aendern: Aendern; anpassen: boolean; schmal: boolean; schliessen: () => void;
}) {
  const bereiche = useAreas();
  // Angeheftete Listen (F-33) - z. B. die Einkaufsliste mit einem Tipp erreichbar.
  const angeheftet = (useAlleListen() ?? []).filter((l) => l.angeheftet && !l.archiviert);
  const gruppen = aufloesen(einstellung);
  const favoriten = favoritenListe(einstellung);

  return (
    <>
      {favoriten.length > 0 && (
        <section aria-label="Favoriten" className="mt-2 flex flex-col gap-0.5">
          <GruppenKopf titel="Favoriten" schmal={schmal} />
          {favoriten.map((e) => <Haupt key={e.id} e={e} schliessen={schliessen} schmal={schmal} />)}
        </section>
      )}

      {gruppen.map((g, i) => (
        <section key={g.id} aria-label={g.titel} className="mt-3 flex flex-col gap-0.5">
          {anpassen ? (
            <div className="flex items-center gap-1 px-1">
              <input aria-label={`Name der Gruppe ${g.titel}`} defaultValue={g.titel} maxLength={40}
                onBlur={(ev) => { if (ev.target.value !== g.titel) void aendern((e) => benenneGruppe(e, g.id, ev.target.value)); }}
                onKeyDown={(ev) => { if (ev.key === 'Enter') ev.currentTarget.blur(); }}
                className="beschriftung min-w-0 flex-1 rounded border border-line bg-surface px-1.5 py-0.5 text-ink-faint focus:border-accent focus:outline-none" />
              <button type="button" className={kleinerKnopf} disabled={i === 0} aria-label={`${g.titel} nach oben`} onClick={() => void aendern((e) => verschiebeGruppe(e, g.id, -1))}><ArrowUp className="size-3.5" /></button>
              <button type="button" className={kleinerKnopf} disabled={i === gruppen.length - 1} aria-label={`${g.titel} nach unten`} onClick={() => void aendern((e) => verschiebeGruppe(e, g.id, 1))}><ArrowDown className="size-3.5" /></button>
            </div>
          ) : (
            <GruppenKopf titel={g.titel} schmal={schmal} eingeklappt={g.eingeklappt} umschalten={() => void aendern((e) => toggleEingeklappt(e, g.id))} />
          )}

          {(anpassen || !g.eingeklappt || schmal) && (g.id === 'bereiche'
            ? <BereicheEintraege bereiche={bereiche} schliessen={schliessen} schmal={schmal} />
            : g.eintraege.map((x, j) => (
              (anpassen || !x.versteckt) && (
                <Fragment key={x.eintrag.id}>
                  <div className="flex items-center gap-0.5">
                    <Haupt e={x.eintrag} schliessen={schliessen} schmal={schmal} gedimmt={x.versteckt} />
                    {anpassen && <Bearbeiten g={g} x={x} j={j} aendern={aendern} />}
                  </div>
                  {x.eintrag.id === 'listen' && !x.versteckt && !schmal && angeheftet.map((l) => (
                    <NavLink key={l.id} to={`/listen/${l.id}`} onClick={schliessen} className={({ isActive }) => unterKlasse(isActive)}>
                      <AreaDot color={l.farbe} size="sm" />
                      <span className="min-w-0 truncate">{l.name}</span>
                    </NavLink>
                  ))}
                </Fragment>
              )
            )))}
        </section>
      ))}

      <div className="mt-auto flex flex-col gap-0.5 pt-2">
        {anpassen && (
          <button type="button" onClick={() => void aendern(() => zuruecksetzen())}
            className="rounded-md px-2.5 py-1.5 text-left text-sm text-ink-faint hover:bg-surface-raised hover:text-ink">
            Auf Standard zurücksetzen
          </button>
        )}
        <Haupt e={{ id: 'einstellungen', to: '/einstellungen', label: de.nav.settings }} icon={Settings} schliessen={schliessen} schmal={schmal} />
      </div>
    </>
  );
}

function GruppenKopf({ titel, schmal, eingeklappt, umschalten }: { titel: string; schmal: boolean; eingeklappt?: boolean; umschalten?: () => void }) {
  // Nur Symbole: statt des Titels eine feine Trennlinie.
  if (schmal) return <hr className="hidden border-line md:block" />;
  const klasse = 'beschriftung flex items-center justify-between rounded px-2.5 py-1 text-ink-quiet';
  return umschalten ? (
    <button type="button" onClick={umschalten} aria-expanded={!eingeklappt} className={cn(klasse, 'hover:text-ink-faint')}>
      {titel}
      <ChevronDown className={cn('size-3.5 transition-transform', eingeklappt && '-rotate-90')} aria-hidden="true" />
    </button>
  ) : <div className={klasse}>{titel}</div>;
}

function Haupt({ e, icon, schliessen, schmal, gedimmt = false }: { e: NavEintrag; icon?: LucideIcon; schliessen: () => void; schmal: boolean; gedimmt?: boolean }) {
  const Icon = icon ?? SYMBOLE[e.id] ?? Layers;
  return (
    <NavLink to={e.to} end={e.end ?? false} onClick={schliessen} title={e.label} className={({ isActive }) => cn(linkKlasse(isActive), gedimmt && 'opacity-50')}>
      {/* Aktive Seite traegt den Akzent (D-05) - am Symbol, nicht als Flaeche. */}
      {({ isActive }) => (
        <>
          <Icon className={cn('size-4 shrink-0', isActive && 'text-accent-text')} />
          <span className={cn('truncate', schmal && 'md:hidden')}>{e.label}</span>
        </>
      )}
    </NavLink>
  );
}

function Bearbeiten({ g, x, j, aendern }: { g: AufgeloesteGruppe; x: AufgeloesteGruppe['eintraege'][number]; j: number; aendern: Aendern }) {
  const name = x.eintrag.label;
  return (
    <>
      <button type="button" className={kleinerKnopf} disabled={j === 0} aria-label={`${name} nach oben`} onClick={() => void aendern((e) => verschiebeEintrag(e, g.id as GruppenId, x.eintrag.id, -1))}><ArrowUp className="size-3.5" /></button>
      <button type="button" className={kleinerKnopf} disabled={j === g.eintraege.length - 1} aria-label={`${name} nach unten`} onClick={() => void aendern((e) => verschiebeEintrag(e, g.id as GruppenId, x.eintrag.id, 1))}><ArrowDown className="size-3.5" /></button>
      <button type="button" className={cn(kleinerKnopf, x.favorit && 'text-accent-text')} aria-pressed={x.favorit} aria-label={`${name} anheften`} onClick={() => void aendern((e) => toggleFavorit(e, x.eintrag.id))}><Star className={cn('size-3.5', x.favorit && 'fill-current')} /></button>
      <button type="button" className={kleinerKnopf} aria-pressed={x.versteckt} aria-label={x.versteckt ? `${name} einblenden` : `${name} ausblenden`} onClick={() => void aendern((e) => toggleVersteckt(e, x.eintrag.id))}>{x.versteckt ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}</button>
    </>
  );
}

function BereicheEintraege({ bereiche, schliessen, schmal }: { bereiche: ReturnType<typeof useAreas>; schliessen: () => void; schmal: boolean }): ReactNode {
  return (
    <>
      {bereiche.map((b) => (
        <Fragment key={b.id}>
          <NavLink to={pfad.bereich(b.id)} onClick={schliessen} title={b.name} className={({ isActive }) => linkKlasse(isActive)}>
            <AreaDot color={b.color} size="sm" />
            <span className={cn('min-w-0 truncate', schmal && 'md:hidden')}>{b.name}</span>
          </NavLink>
          {b.fest === 'gesundheit' && !schmal && (
            <>
              <NavLink to="/training" onClick={schliessen} className={({ isActive }) => unterKlasse(isActive)}><Dumbbell className="size-3.5" /> Training</NavLink>
              <NavLink to="/ernaehrung" onClick={schliessen} className={({ isActive }) => unterKlasse(isActive)}><Salad className="size-3.5" /> Ernährung</NavLink>
            </>
          )}
        </Fragment>
      ))}
      <Link to="/bereiche?neu=1" onClick={schliessen} title={de.areas.add}
        className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-ink-faint transition-colors hover:bg-surface-raised hover:text-ink">
        <Plus className="size-4 shrink-0" /> <span className={schmal ? 'md:hidden' : ''}>{de.areas.add}</span>
      </Link>
      {!schmal && (
        <NavLink to="/bereiche" end onClick={schliessen} className={({ isActive }) => unterKlasse(isActive)}>
          <Layers className="size-3.5" /> Alle Bereiche verwalten
        </NavLink>
      )}
    </>
  );
}
